<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseButton, FieldSelect } from '@magma-app/magma-lapilli';

// Stores
import { useUserStore } from '@/stores/user';
import { useCommonStore } from '@/stores/common';
import { useAsyncAction } from '@/composables/useAsyncAction';
const userStore = useUserStore();
const commonStore = useCommonStore();
const { isRunning: isSaving, run } = useAsyncAction();

// Plugins
const { locale, t } = useI18n({ useScope: 'global' });

// Reactive variables
const formData = reactive({
  language: userStore.user?.language || locale.value || 'en',
  textNotifications: userStore.user?.acceptPhoneNotifications ?? true,
  emailNotifications: userStore.user?.acceptEmailNotifications ?? true,
  email: userStore.user?.email || '',
});

const formDataSaved = ref(JSON.stringify(formData));

const languageOptions = [
  { label: 'Français', value: 'fr' },
  { label: 'English', value: 'en' },
];

// Methods
const hasChanges = () => {
  return formDataSaved.value !== JSON.stringify(formData);
};

const getChangedPreferences = () => {
  const savedData = JSON.parse(formDataSaved.value);
  const changedFields: { [key: string]: string | boolean } = {};

  // Map form fields to API field names
  const fieldMapping = {
    language: 'language',
    textNotifications: 'acceptPhoneNotifications',
    emailNotifications: 'acceptEmailNotifications',
    email: 'notificationEmail',
  };

  // Compare each field and add only changed ones
  Object.entries(fieldMapping).forEach(([formField, apiField]) => {
    if (formData[formField as keyof typeof formData] !== savedData[formField]) {
      changedFields[apiField] = formData[formField as keyof typeof formData];
    }
  });

  return changedFields;
};

const savePreferences = () => run(async () => {
  if (!hasChanges()) return;

  try {
    const changedFields = getChangedPreferences();

    // Only make API call if there are actual changes
    if (Object.keys(changedFields).length > 0) {
      await userStore.updateUser(changedFields);

      // Update locale if language changed
      if (changedFields.language && changedFields.language !== locale.value) {
        locale.value = changedFields.language as string;
      }

      formDataSaved.value = JSON.stringify(formData);

      // Show success toast notification
      commonStore.setAlert({
        open: true,
        type: 'success',
        title: t('generic.informationUpdated'),
      });
    }
  } catch (error) {
    console.log(error);
  }
});

const onNotificationsChange = (
  value: boolean,
  id: 'textNotifications' | 'emailNotifications'
) => {
  // Ensure at least one notification method is enabled
  if (id === 'textNotifications' && !value && !formData.emailNotifications) {
    formData.emailNotifications = true;
  } else if (
    id === 'emailNotifications' &&
    !value &&
    !formData.textNotifications
  ) {
    formData.textNotifications = true;
  }
};
</script>

<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-row gap-8 items-start justify-between">
      <h2 class="text-2xl font-bold leading-7 text-foreground-emphasis w-2/3">
        {{ $t('settingsPreferenceView.title') }}
      </h2>
      <!-- IF THERE'S NO MODIFICATIONS: DISABLE THE BUTTON -->
      <BaseButton
        :text="$t('generic.save')"
        size="md"
        class="w-1/3"
        :disabled="isSaving || !hasChanges()"
        @click="savePreferences"
      />
    </div>
    <div class="flex flex-col gap-4">
      <FieldSelect
        id="language"
        v-model="formData.language"
        :locale="$i18n.locale"
        :label="$t('helperOnboarding.appLanguages.title')"
        :options="languageOptions"
      />
    </div>
  </div>
</template>
